import { motion } from "framer-motion";
import { FlaskConical, Radio, Bot, GraduationCap } from "lucide-react";

const USES = [
  {
    icon: FlaskConical,
    t: "REHEARSE IDEAS SAFELY",
    d: "Close a clinic, raise the rents, send a storm, then watch a whole community adapt. Policies get rehearsals before reality, not just forecasts.",
  },
  {
    icon: Radio,
    t: "TRACE HOW INFORMATION SPREADS",
    d: "Knowledge moves by word of mouth and by the Saturday newspaper. We can measure how far a fact travels, how it distorts on the way, and where it gets stuck.",
  },
  {
    icon: Bot,
    t: "OBSERVE AI IN SOCIETY",
    d: "Language models that chat and decide can be watched inside a small, safe society: how they behave, where they fail, and whether people can trust them.",
  },
  {
    icon: GraduationCap,
    t: "MAKE SOCIAL SCIENCE TANGIBLE",
    d: "A living miniature of everyday life for students, teachers and the curious: a society you can watch thinking, one resident at a time.",
  },
];

export default function Research() {
  return (
    <section id="research" className="py-24 sm:py-32 bg-gb-bg2 border-y-2 border-gb-line relative overflow-hidden">
      <div className="absolute inset-0 dot-bg opacity-60 pointer-events-none" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-10"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">03 · WHY SIMULATE</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            Why simulate a town?
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="max-w-3xl text-base sm:text-lg leading-relaxed text-gb-mint mb-12"
        >
          Because some questions can't be tested on real people, and some
          mistakes shouldn't reach them.
        </motion.p>

        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          {USES.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.t}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.6 }}
                className="bg-gb-bg border-2 border-gb-line hover:border-gb-accent/50 p-6 sm:p-7 transition-colors duration-300"
                data-testid={`use-case-${i + 1}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 border-2 border-gb-accent/50 text-gb-neon">
                    <Icon size={17} />
                  </span>
                  <h3 className="font-title text-2xl text-gb-pale tracking-wide">{p.t}</h3>
                </div>
                <p className="text-[13px] leading-relaxed text-gb-light/85">{p.d}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl text-sm leading-relaxed text-gb-mint/90"
        >
          That is what the simulated city is for: a place to learn from
          carefully, before the world learns the hard way.
        </motion.p>
      </div>
    </section>
  );
}
